import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppearance } from '../../contexts/AppearanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveIdentityRoles } from '../../lib/auth/identityRoles';
import {
  canEnterWorkspaceSurface,
  isFocusedAssignmentParticipant,
  resolveFocusedAssignmentSurface,
  type WorkspaceSurfaceId,
} from '../../lib/auth/workspaceSurfaceEntry';
import {
  buildOnboardingHref,
  normalizeReturnSurface,
} from '../../lib/navigation/returnTarget';
import { getAccountCapabilities } from '../../lib/auth/capabilities';
import { listAccessibleWorkspaces, resolveWorkspaceLanding } from '@/lib/auth/accessibleWorkspaces';
import { lookupRuntimeWorkspace } from '@/lib/auth/runtimeWorkspaceLookup';
import { customerRouteHref, type CustomerRouteTarget } from '@/lib/navigation/canonicalRoutes';
import { onboardingService } from '@/services/OnboardingService';
import {
  beginContextResolution,
  clearWorkspaceContextHint,
  isCurrentContextResolution,
  readWorkspaceContextHint,
  rememberWorkspaceContextHint,
  resolveContextDestination,
} from '@/lib/auth/contextSwitching';

/**
 * HomeRedirect component that redirects to user's preferred home page
 * Falls back to dashboard if context isn't ready yet
 */
export const HomeRedirect: React.FC = () => {
  const { homePage } = useAppearance();
  const { user } = useAuth();
  const [onboardingRedirect, setOnboardingRedirect] = React.useState<string | null>(null);
  const [selectionHint, setSelectionHint] = React.useState<string | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = React.useState(true);
  const accountRole = resolveIdentityRoles(user).accountRole;
  const platformRole = resolveIdentityRoles(user).platformRole;
  const homePageRoutes: Record<string, CustomerRouteTarget> = {
    dashboard: 'dashboard',
    projects: 'projects',
    tasks: 'tasks',
  };
  const configuredTarget = homePageRoutes[homePage] || 'dashboard';
  const accountCapabilities = React.useMemo(() => getAccountCapabilities(user), [user]);
  const focusedAssignmentTarget = React.useMemo(() => (
    accountRole === 'organization_member'
    && configuredTarget === 'dashboard'
    && isFocusedAssignmentParticipant(user)
      ? resolveFocusedAssignmentSurface(user, accountCapabilities)
      : null
  ), [accountCapabilities, accountRole, configuredTarget, user]);
  const preferredTarget = focusedAssignmentTarget ?? configuredTarget;
  const preferredSurface = normalizeReturnSurface(preferredTarget) ?? 'dashboard';

  const landing = React.useMemo(() => {
    if (!user || platformRole || !accountRole) return null;
    return resolveWorkspaceLanding(listAccessibleWorkspaces(user, (id) => lookupRuntimeWorkspace(id, user)));
  }, [accountRole, platformRole, user]);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!user || platformRole || !accountRole) {
        if (!cancelled) {
          setOnboardingRedirect(null);
          setSelectionHint(null);
          setCheckingOnboarding(false);
        }
        return;
      }

      const resolutionToken = beginContextResolution(user.id);
      const isCurrent = () => !cancelled && isCurrentContextResolution(user.id, resolutionToken);

      try {
        if (!landing) {
          if (isCurrent()) {
            setOnboardingRedirect(buildOnboardingHref(null, preferredSurface));
            setSelectionHint(null);
            setCheckingOnboarding(false);
          }
          return;
        }

        const savedHint = readWorkspaceContextHint(user.id);
        const destination = resolveContextDestination({
          landing,
          requestedWorkspaceId: savedHint?.workspaceId,
          source: 'saved',
        });

        if (destination.kind === 'none') {
          clearWorkspaceContextHint(user.id);
          if (isCurrent()) {
            setOnboardingRedirect(null);
            setSelectionHint(null);
            setCheckingOnboarding(false);
          }
          return;
        }

        if (destination.kind === 'denied') {
          clearWorkspaceContextHint(user.id);
          if (isCurrent()) {
            setOnboardingRedirect('/unauthorized');
            setSelectionHint(null);
            setCheckingOnboarding(false);
          }
          return;
        }

        const requestedWorkspaceId = destination.kind === 'selector'
          ? destination.requestedWorkspaceId ?? null
          : null;

        if (destination.kind === 'scoped') {
          const workspace = landing.kind === 'one'
            ? landing.workspace
            : landing.kind === 'many'
              ? landing.workspaces.find((candidate) => candidate.id === destination.workspaceId)
              : null;
          if (workspace) {
            rememberWorkspaceContextHint(user.id, workspace, 'saved');
          }

          const state = await onboardingService.initialize(destination.workspaceId);
          if (isCurrent()) {
            setSelectionHint(null);
            setOnboardingRedirect(
              state.isComplete
                ? null
                : buildOnboardingHref(destination.workspaceId, preferredSurface),
            );
            setCheckingOnboarding(false);
          }
          return;
        }

        const contexts = await onboardingService.getContexts();
        let pendingExists = false;
        for (const context of contexts) {
          const state = await onboardingService.initialize(context.workspaceId);
          if (!state.isComplete) {
            pendingExists = true;
            break;
          }
        }

        if (isCurrent()) {
          setSelectionHint(requestedWorkspaceId);
          setOnboardingRedirect(
            pendingExists ? buildOnboardingHref(null, preferredSurface) : null,
          );
          setCheckingOnboarding(false);
        }
      } catch {
        if (isCurrent()) {
          setOnboardingRedirect(buildOnboardingHref(null, preferredSurface));
          setSelectionHint(null);
          setCheckingOnboarding(false);
        }
      }
    };

    setCheckingOnboarding(true);
    void run();

    return () => {
      cancelled = true;
    };
  }, [accountRole, landing, platformRole, preferredSurface, user]);

  if (checkingOnboarding) {
    return null;
  }

  if (onboardingRedirect) {
    return <Navigate to={onboardingRedirect} replace />;
  }

  // Platform identities route to the Control Plane only.
  if (platformRole) {
    return <Navigate to="/platform" replace />;
  }

  if (!accountRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Intended customer surface from the saved home preference.
  // Many workspaces: preserve the intended surface through the selector.
  if (landing?.kind === 'many') {
    const requested = selectionHint
      ? `requestedWorkspaceId=${encodeURIComponent(selectionHint)}&`
      : '';
    return (
      <Navigate
        to={`/workspaces/select?${requested}returnTo=${encodeURIComponent(preferredSurface)}`}
        replace
      />
    );
  }

  // Single workspace: land on the intended surface when enterable, else the
  // safe Dashboard. Never bounce a valid user to a blank selector.
  const caps = accountCapabilities;
  const canUsePreferred =
    preferredTarget === 'dashboard'
    || canEnterWorkspaceSurface(preferredTarget as WorkspaceSurfaceId, user, caps);
  const landingTarget = canUsePreferred ? preferredTarget : 'dashboard';
  const targetPath = landing?.kind === 'one'
    ? customerRouteHref(landing.workspace.id, landingTarget)
    : '/workspaces/select';

  return <Navigate to={targetPath} replace />;
};

export default HomeRedirect;
